import type { Incident, Unit, MajorIncident } from '../types';

const DB_NAME = 'C2CommandSystemDB';
const DB_VERSION = 1;

interface PendingSync {
  id: string;
  type: 'CREATE' | 'UPDATE' | 'DELETE';
  entity: 'incident' | 'unit' | 'majorIncident' | 'log';
  data: any;
  timestamp: number;
  retryCount: number;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('incidents')) {
          db.createObjectStore('incidents', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('units')) {
          db.createObjectStore('units', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('majorIncidents')) {
          db.createObjectStore('majorIncidents', { keyPath: 'id' });
        }

        if (!db.objectStoreNames.contains('pendingSync')) {
          const syncStore = db.createObjectStore('pendingSync', { keyPath: 'id' });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('metadata')) {
          db.createObjectStore('metadata', { keyPath: 'key' });
        }
      };
    });
  }

  // Generic CRUD operations
  private async getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
    if (!this.db) await this.init();
    const transaction = this.db!.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  async get<T>(storeName: string, id: string): Promise<T | null> {
    const store = await this.getStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll<T>(storeName: string): Promise<T[]> {
    const store = await this.getStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async set(storeName: string, data: any): Promise<void> {
    const store = await this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName: string, id: string): Promise<void> {
    const store = await this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName: string): Promise<void> {
    const store = await this.getStore(storeName, 'readwrite');
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Incident operations
  async saveIncident(incident: Incident): Promise<void> {
    await this.set('incidents', incident);
  }

  async getIncident(id: string): Promise<Incident | null> {
    return this.get<Incident>('incidents', id);
  }

  async getAllIncidents(): Promise<Incident[]> {
    return this.getAll<Incident>('incidents');
  }

  async deleteIncident(id: string): Promise<void> {
    await this.delete('incidents', id);
  }

  // Unit operations
  async saveUnit(unit: Unit): Promise<void> {
    await this.set('units', unit);
  }

  async getUnit(id: string): Promise<Unit | null> {
    return this.get<Unit>('units', id);
  }

  async getAllUnits(): Promise<Unit[]> {
    return this.getAll<Unit>('units');
  }

  async deleteUnit(id: string): Promise<void> {
    await this.delete('units', id);
  }

  // Major Incident operations
  async saveMajorIncident(incident: MajorIncident): Promise<void> {
    await this.set('majorIncidents', incident);
  }

  async getMajorIncident(id: string): Promise<MajorIncident | null> {
    return this.get<MajorIncident>('majorIncidents', id);
  }

  async getAllMajorIncidents(): Promise<MajorIncident[]> {
    return this.getAll<MajorIncident>('majorIncidents');
  }

  async deleteMajorIncident(id: string): Promise<void> {
    await this.delete('majorIncidents', id);
  }

  // Pending sync operations
  async addPendingSync(sync: Omit<PendingSync, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
    const pendingSync: PendingSync = {
      ...sync,
      id: `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retryCount: 0,
    };
    await this.set('pendingSync', pendingSync);
  }

  async getPendingSync(): Promise<PendingSync[]> {
    return this.getAll<PendingSync>('pendingSync');
  }

  async deletePendingSync(id: string): Promise<void> {
    await this.delete('pendingSync', id);
  }

  async updatePendingSyncRetry(id: string): Promise<void> {
    const sync = await this.get<PendingSync>('pendingSync', id);
    if (sync) {
      sync.retryCount++;
      await this.set('pendingSync', sync);
    }
  }

  // Metadata operations
  async setMetadata(key: string, value: any): Promise<void> {
    await this.set('metadata', { key, value, updatedAt: Date.now() });
  }

  async getMetadata(key: string): Promise<any> {
    const result = await this.get<{ key: string; value: any }>('metadata', key);
    return result?.value || null;
  }

  async setLastSync(timestamp: number): Promise<void> {
    await this.setMetadata('lastSync', timestamp);
  }

  async getLastSync(): Promise<number | null> {
    return this.getMetadata('lastSync');
  }

  // Bulk operations for initial sync
  async bulkSaveIncidents(incidents: Incident[]): Promise<void> {
    const store = await this.getStore('incidents', 'readwrite');
    await Promise.all(incidents.map(incident => 
      new Promise<void>((resolve, reject) => {
        const request = store.put(incident);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ));
  }

  async bulkSaveUnits(units: Unit[]): Promise<void> {
    const store = await this.getStore('units', 'readwrite');
    await Promise.all(units.map(unit => 
      new Promise<void>((resolve, reject) => {
        const request = store.put(unit);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ));
  }

  async bulkSaveMajorIncidents(incidents: MajorIncident[]): Promise<void> {
    const store = await this.getStore('majorIncidents', 'readwrite');
    await Promise.all(incidents.map(incident => 
      new Promise<void>((resolve, reject) => {
        const request = store.put(incident);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      })
    ));
  }

  // Clear all data
  async clearAll(): Promise<void> {
    await Promise.all([
      this.clear('incidents'),
      this.clear('units'),
      this.clear('majorIncidents'),
      this.clear('pendingSync'),
      this.clear('metadata'),
    ]);
  }
}

export const offlineStorage = new OfflineStorage();
